// src/components/Layout.jsx — App shell with sidebar (desktop) + bottom tab nav (mobile)
import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './layout.css';

// ── Inline SVG icon set ────────────────────────────────────────────────────

function Ico({ children, size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

const Icons = {
  portfolio: <Ico><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></Ico>,
  watchlist: <Ico><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></Ico>,
  screener:  <Ico><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></Ico>,
  orders:    <Ico><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></Ico>,
  charts:    <Ico><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></Ico>,
  margins:   <Ico><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></Ico>,
  settings:  <Ico><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></Ico>,
  stocks:    <Ico><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></Ico>,
  daily:     <Ico><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Ico>,
  quotes:    <Ico><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></Ico>,
  analysis:  <Ico><path d="M2 20h20" /><path d="M5 20V10l4-4 4 4 4-6 4 6v10" /></Ico>,
  expense:   <Ico><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><line x1="6" y1="15" x2="10" y2="15" /><line x1="14" y1="15" x2="18" y2="15" /></Ico>,
  more:      <Ico><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></Ico>,
};

// ── Nav items ──────────────────────────────────────────────────────────────

const ALL_NAV = [
  { to: '/',                label: 'Portfolio',    icon: 'portfolio', end: true },
  { to: '/analysis',        label: 'Analysis',     icon: 'analysis'  },
  { to: '/watchlist',       label: 'Watchlist',    icon: 'watchlist' },
  { to: '/screener',        label: 'Screener',     icon: 'screener'  },
  { to: '/orders',          label: 'Orders',       icon: 'orders'    },
  { to: '/charts',          label: 'Charts',       icon: 'charts'    },
  { to: '/margins',         label: 'Margins',      icon: 'margins'   },
  { to: '/stocks',          label: 'Stock List',   icon: 'stocks'    },
  { to: '/daily',           label: 'Daily Data',   icon: 'daily'     },
  { to: '/quotes',          label: 'Live Quotes',  icon: 'quotes'    },
  { to: '/expense-tracker', label: 'Expenses',     icon: 'expense'   },
  { to: '/settings',        label: 'Settings',     icon: 'settings'  },
];

// 5 primary tabs visible in the bottom bar
const PRIMARY_NAV = [
  { to: '/',                label: 'Portfolio', icon: 'portfolio', end: true },
  { to: '/charts',          label: 'Charts',    icon: 'charts'    },
  { to: '/expense-tracker', label: 'Expenses',  icon: 'expense'   },
  { to: '/screener',        label: 'Screener',  icon: 'screener'  },
  { to: '/settings',        label: 'Settings',  icon: 'settings'  },
];

// Remaining items in the "More" drawer
const MORE_NAV = [
  { to: '/analysis',  label: 'Analysis',    icon: 'analysis'  },
  { to: '/watchlist', label: 'Watchlist',   icon: 'watchlist' },
  { to: '/orders',    label: 'Orders',      icon: 'orders'    },
  { to: '/margins',   label: 'Margins',     icon: 'margins'   },
  { to: '/stocks',    label: 'Stocks',      icon: 'stocks'    },
  { to: '/daily',     label: 'Daily Data',  icon: 'daily'     },
  { to: '/quotes',    label: 'Live Quotes', icon: 'quotes'    },
];

// ── Sidebar (desktop) ──────────────────────────────────────────────────────

function Sidebar({ username, onLogout }) {
  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img
          src="/logo.png"
          alt="TraderAIgo"
          className="sidebar-logo-img"
          onError={e => { e.target.style.display = 'none'; }}
        />
        Trader<span>AIgo</span>
      </div>
      <nav className="sidebar-nav">
        {ALL_NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
          >
            {Icons[item.icon]}
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <span className="sidebar-username">{username}</span>
          <button className="sidebar-logout" onClick={onLogout}>Logout</button>
        </div>
      </div>
    </aside>
  );
}

// ── Mobile bottom tab bar + More drawer ───────────────────────────────────

function MobileNav() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const isMoreActive = MORE_NAV.some(item =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
  );

  const close = () => setDrawerOpen(false);

  return (
    <>
      {/* Bottom tab bar — shown only on mobile via .bottom-nav CSS */}
      <nav className="bottom-nav">
        {PRIMARY_NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={close}
            className={({ isActive }) => 'bottom-tab' + (isActive ? ' active' : '')}
          >
            {React.cloneElement(Icons[item.icon], { size: 20 })}
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* More button */}
        <button
          className={'bottom-tab' + (isMoreActive || drawerOpen ? ' active' : '')}
          onClick={() => setDrawerOpen(o => !o)}
        >
          {React.cloneElement(Icons.more, { size: 20 })}
          <span>More</span>
        </button>
      </nav>

      {/* Backdrop */}
      {drawerOpen && (
        <div className="drawer-backdrop" onClick={close} />
      )}

      {/* More drawer */}
      {drawerOpen && (
        <div className="drawer-panel">
          <div className="drawer-header">
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0' }}>More</span>
            <button
              onClick={close}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.1rem', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
          <div className="drawer-grid">
            {MORE_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={close}
                className={({ isActive }) => 'drawer-item' + (isActive ? ' active' : '')}
              >
                {React.cloneElement(Icons[item.icon], { size: 22 })}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Layout (exported) ──────────────────────────────────────────────────────

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-shell" style={{ background: '#0f172a' }}>
      <Sidebar username={user?.username} onLogout={handleLogout} />
      <main className="main-content">{children}</main>
      <MobileNav />
    </div>
  );
}
